<template>
	<div class="content-page content-page--vendors">
		<div class="content-page__heading" ref="contentPageHeading">
			<div class="content-page__breadcrumb-title">
				<h2 class="content-page__title">3P Vendor Manager</h2>
			</div>
			<div class="content-page__actions">
				<Button
					ref="addBtn"
					className="btn--has-icon"
					iconClassName="icon-more"
					labelText="Add"
					:btnClick="addVendor"
					:iconBeforeLabel="true"
					v-if="validatePermission('vendors.create')"
				/>
			</div>
		</div>
		<div
			class="results-toolbar results-toolbar--variant-seven"
			ref="resultsToolBar"
		>
			<div class="results-toolbar__search">
				<Search
					variant="toggle-icons"
					placeholder="Search by name, alias, or party ID"
					v-model="filters.search"
				/>
			</div>
			<div class="results-toolbar__sort">
				<DropdownSort
					:options="ddsOptions"
					:showClearSelectedButton="false"
					v-model="ddSortByValue"
				/>
			</div>
			<div class="results-toolbar__filter">
				<DropdownV2
					defaultValue="all"
					:options="ddStatusOptions"
					v-model="filters.status"
				/>
			</div>
			<div class="results-toolbar__counter">
				<ResultCounter v-if="hasResults" :counter="resultCounter" />
			</div>
		</div>
		<ScrollContainer
			v-if="hasResults"
			variant="infinity-scroll"
			:hasMoreItems="hasMoreItemsToLoad"
			@scrollEnd="handleScrollEnd"
		>
			<VendorsList :items="integrators.items" />
		</ScrollContainer>
		<Spinner
			v-if="loading"
			:processor="{ loading: true }"
			data-test="spinner"
		/>
		<NoResults v-if="!loading && !hasResults" data-test="noResults" />
		<NewVendor
			v-model="displayVendorModal"
			v-if="displayVendorModal"
			ref="newVendorModal"
		/>
	</div>
</template>

<script>
import Button from '@/components/forms/button/button';
import DropdownV2 from '@/components/forms/dropdown/dropdown-v2.vue';
import DropdownSort from '@/components/forms/dropdown/dropdown-sort';
import ResultCounter from '@/site-components/result-counter/result-counter';
import Search from '@/components/forms/search/search';
import VendorsList from './vendors-summary-list';
import NewVendor from '../settings/vendor-add.vue';
import Spinner from '@/components/visual/spinner/spinner';
import NoResults from '@/site-components/no-results/no-results';
import ScrollContainer from '@/components/shells/scroll-container/scroll-container';

// vuex
import { mapState, mapActions, mapGetters } from 'vuex';

// libraries
import _ from 'lodash';

export default {
	name: 'VendorsSummaryPage',
	components: {
		Button,
		DropdownV2,
		DropdownSort,
		ResultCounter,
		Search,
		NewVendor,
		Spinner,
		VendorsList,
		NoResults,
		ScrollContainer
	},
	props: {
		search: String,
		sortBy: { type: String },
		sortDirection: { type: String },
		status: String,
		count: { type: Number }
	},
	data() {
		return {
			filters: {
				search: this.search,
				sortby: this.sortBy,
				sortdirection: this.sortDirection,
				status: this.status
				// page: 1,
				// count: this.count
			},
			defaultFilters: {
				search: '',
				sortby: 'name',
				sortdirection: 'asc',
				status: 'active'
				// page: 1,
				// count: 100
			},
			page: 1,
			countPerPage: this.count,
			ddStatusOptions: [
				{
					text: 'All Statuses',
					value: 'all'
				},
				{
					text: 'Active',
					value: 'active'
				},
				{
					text: 'Inactive',
					value: 'inactive'
				},
				{
					text: 'Error',
					value: 'errors'
				}
			],
			ddsOptions: [
				{ text: 'Name', value: 'name' },
				{ text: 'Party ID', value: 'integratorcode' },
				{ text: 'Alias', value: 'alias' }
			],
			displayVendorModal: false,
			loading: true,
			loadingMore: false
		};
	},
	computed: {
		...mapState('integrators', ['integrators']),
		...mapGetters('integrators', ['showDependentVendorModal']),
		ddSortByValue: {
			get() {
				return {
					sortby: this.filters.sortby,
					sortdirection: this.filters.sortdirection
				};
			},
			set(value) {
				this.filters.sortby = value.sortby;
				this.filters.sortdirection = value.sortdirection;
			}
		},
		hasResults() {
			return this.integrators.items?.length && !this.loading;
		},
		resultCounter() {
			return `${this.integrators.items?.length} of ${this.integrators.totalCount}`;
		},
		hasMoreItemsToLoad() {
			return _.isEmpty(this.integrators)
				? false
				: this.page < this.integrators.totalPageCount;
		}
	},
	methods: {
		...mapActions('integrators', ['loadVendors']),
		addVendor() {
			this.displayVendorModal = true;
		},
		async fetchData(page) {
			if (!this.loadingMore) {
				this.loading = true;
			}
			let query = _.cloneDeep(this.filters);
			query.page = page;
			query.count = this.countPerPage || 100;

			await this.loadVendors(query);
			this.loading = false;
			this.loadingMore = false;
		},
		updateRoute(queries) {
			this.$router
				.push({
					path: '/vendors',
					query: {
						search: queries.search || undefined,
						sortBy: queries.sortby,
						sortDirection: queries.sortdirection,
						status: queries.status || undefined,
						count: this.countPerPage || undefined
					}
				})
				.catch(() => {});
		},
		showFlyout() {
			return !_.isEmpty(this.vendor);
		},
		updateToast(val) {
			this.$store.dispatch('toastMessages/show', {
				toastId: 'vendors.action',
				statusCode: val.status,
				message: val.message,
				slot: 'default',
				useIcon: true,
				userDismissable: true
			});
		},
		handleScrollEnd() {
			if (this.hasMoreItemsToLoad) {
				this.loadingMore = true;
				this.page += 1;
			}
		}
	},
	watch: {
		filters: {
			deep: true,
			immediate: true,
			handler(newValue) {
				this.updateRoute(newValue);
				if (this.page != 1) {
					this.page = 1;
				} else {
					this.fetchData(1);
				}
			}
		},
		page: {
			handler(page) {
				this.fetchData(page);
			}
		},
		// fallback to default route params and queries
		'$route.query': {
			handler(newQuery) {
				if (_.isEmpty(newQuery)) {
					this.filters = _.cloneDeep(this.defaultFilters);
				}
			}
		}
	}
};
</script>

<style lang="scss" scoped>
/**
	* Vendors Page
	* Can use SCSS variables = TRUE
    * This should only contain classes and styles used for the page layout.
*/

::v-deep .results-toolbar .results-toolbar__search {
	@include mq($from: md) {
		width: 300px;
	}
}

.content-page {
	&__heading {
		@include mq($until: sm-l) {
			flex-direction: column;
			align-items: unset;
		}
	}
	&__actions {
		@include mq($until: md) {
			margin-top: $gutter-md;
		}
	}
}
</style>
